import { useState, useEffect, useCallback, useMemo } from 'react';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { memberId } from '@trello/session-cookie';
import { useSharedState } from '@trello/shared-state';

// eslint-disable-next-line no-restricted-imports
import { boardsMenuState } from 'app/gamma/src/modules/state/ui/boards-menu-state';

import { useStarredBoardsQuery } from './StarredBoardsQuery.generated';
import { useStarredBoardButtonAddStarMutation } from './StarredBoardButtonAddStarMutation.generated';
import { useStarredBoardButtonRemoveStarMutation } from './StarredBoardButtonRemoveStarMutation.generated';

const sendSuccessBoardStarAnalytic = (idBoard: string, value: boolean) => {
  Analytics.sendUpdatedBoardFieldEvent({
    field: 'star',
    value,
    source: 'boardsMenuInlineDialog',
    containers: {
      board: {
        id: idBoard,
      },
    },
  });
};

const sendErrorBoardStarAnalytic = (idBoard: string, error: Error) => {
  Analytics.sendOperationalEvent({
    action: 'errored',
    actionSubject: 'error',
    source: 'boardsMenuInlineDialog',
    containers: formatContainers({ idBoard }),
    attributes: {
      message: error?.message,
      name: error?.name,
    },
  });
};

export const useStarredBoards = () => {
  const [boardsMenu, setBoardsMenu] = useSharedState(boardsMenuState);
  const [optimisticBoardMenu, setOptimisticBoardMenu] = useState(boardsMenu);

  const [
    starredBoardButtonAddStarMutation,
  ] = useStarredBoardButtonAddStarMutation();
  const [
    starredBoardButtonRemoveStarMutation,
  ] = useStarredBoardButtonRemoveStarMutation();

  const { data: starredBoardsData } = useStarredBoardsQuery({
    variables: {
      memberId: memberId || 'me',
    },
  });

  const starredBoardIds = useMemo(
    () =>
      starredBoardsData?.member?.boardStars.map(({ idBoard }) => idBoard) ?? [],
    [starredBoardsData?.member?.boardStars],
  );

  // sync apollo state with local state
  useEffect(() => {
    setOptimisticBoardMenu((prevBoardMenu) => ({
      ...prevBoardMenu,
      idBoardsStarred: starredBoardIds,
    }));
  }, [starredBoardIds]);

  // sync local state with shared state
  useEffect(() => {
    setBoardsMenu(optimisticBoardMenu);
  }, [optimisticBoardMenu, setBoardsMenu]);

  const removeStar = useCallback(
    async (idBoard: string) => {
      if (!memberId) {
        return;
      }
      const { id: boardStarId } =
        starredBoardsData?.member?.boardStars.find(
          (boardStar) => boardStar.idBoard === idBoard,
        ) ?? {};

      if (!boardStarId) {
        return;
      }

      setOptimisticBoardMenu((prevBoardMenu) => ({
        ...prevBoardMenu,
        idBoardsStarred: prevBoardMenu.idBoardsStarred.filter(
          (id) => id !== idBoard,
        ),
      }));

      try {
        await starredBoardButtonRemoveStarMutation({
          variables: {
            memberId,
            boardStarId,
          },
        });
        sendSuccessBoardStarAnalytic(idBoard, false);
      } catch (error) {
        sendErrorBoardStarAnalytic(idBoard, error);
        // undo optimistic update
        setOptimisticBoardMenu((prevBoardMenu) => ({
          ...prevBoardMenu,
          idBoardsStarred: starredBoardIds,
        }));
      }
    },
    [
      starredBoardButtonRemoveStarMutation,
      starredBoardsData?.member?.boardStars,
      starredBoardIds,
    ],
  );

  const addStar = useCallback(
    async (idBoard: string) => {
      if (!memberId) {
        return;
      }
      const maxPos =
        starredBoardsData?.member?.boardStars.reduce(
          (acc, boardStar): number => Math.max(acc, boardStar.pos),
          0,
        ) ?? 0;

      setOptimisticBoardMenu((prevBoardMenu) => ({
        ...prevBoardMenu,
        idBoardsStarred: [...prevBoardMenu.idBoardsStarred, idBoard],
      }));

      try {
        await starredBoardButtonAddStarMutation({
          variables: {
            memberId,
            boardId: idBoard,
            pos: maxPos + 1,
          },
        });
        sendSuccessBoardStarAnalytic(idBoard, true);
      } catch (error) {
        sendErrorBoardStarAnalytic(idBoard, error);
        // undo optimistic update
        setOptimisticBoardMenu((prevBoardMenu) => ({
          ...prevBoardMenu,
          idBoardsStarred: starredBoardIds,
        }));
      }
    },
    [
      starredBoardButtonAddStarMutation,
      starredBoardsData?.member?.boardStars,
      starredBoardIds,
    ],
  );

  return { boardsMenu, addStar, removeStar };
};
